import type { ClipboardEvent, KeyboardEvent } from 'react';
import type { TextFieldProps } from './TextField';

type NumericTextFieldProps = Pick<
  TextFieldProps,
  'type' | 'inputMode' | 'min' | 'step' | 'onKeyDown' | 'onPaste' | 'transformValue'
>;

const CONTROL_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Enter',
  'Escape',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);

export const positiveIntegerFieldProps: NumericTextFieldProps = {
  type: 'number',
  inputMode: 'numeric',
  min: 1,
  step: 1,
  onKeyDown: blockNonIntegerKey,
  onPaste: blockNonIntegerPaste,
  transformValue: onlyDigits,
};

/** Same as {@link positiveIntegerFieldProps}, for counters where 0 is valid. */
export const nonNegativeIntegerFieldProps: NumericTextFieldProps = {
  ...positiveIntegerFieldProps,
  min: 0,
};

/**
 * A money amount: non-negative, at most two decimals, left blank when unknown.
 *
 * A text input rather than `type="number"` on purpose — a number input hands
 * back '' for anything it considers malformed, which silently eats a half-typed
 * "12." and makes a comma impossible to correct. Instead the value is kept as
 * typed and normalised: a comma becomes a dot (Romanian keyboards produce one),
 * and anything that is not a digit or the first dot is dropped, so the field
 * can only ever hold something the API will accept.
 */
export const moneyFieldProps: NumericTextFieldProps = {
  inputMode: 'decimal',
  transformValue: onlyMoney,
};

function onlyMoney(value: string): string {
  const cleaned = value.replace(',', '.').replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  if (rest.length === 0) return whole ?? '';
  return `${whole}.${rest.join('').slice(0, 2)}`;
}

function blockNonIntegerKey(event: KeyboardEvent<HTMLInputElement>): void {
  if (event.ctrlKey || event.metaKey || event.altKey || CONTROL_KEYS.has(event.key)) return;
  if (!/^\d$/.test(event.key)) event.preventDefault();
}

function blockNonIntegerPaste(event: ClipboardEvent<HTMLInputElement>): void {
  const text = event.clipboardData.getData('text');
  if (!/^\d+$/.test(text)) event.preventDefault();
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}
